import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const ODV2_TOOLTIP_OFFSET = 6;

function getOdv2TooltipCoords(rect, placement) {
  switch (placement) {
    case 'above':
      return {
        top: rect.top - ODV2_TOOLTIP_OFFSET,
        left: rect.left + rect.width / 2,
        transform: 'translate(-50%, -100%)',
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + ODV2_TOOLTIP_OFFSET,
        transform: 'translateY(-50%)',
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - ODV2_TOOLTIP_OFFSET,
        transform: 'translate(-100%, -50%)',
      };
    default:
      return {
        top: rect.bottom + ODV2_TOOLTIP_OFFSET,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
  }
}

export function Odv2HoverTooltip({
  text,
  content,
  children,
  className = '',
  placement = 'below',
  selectable = false,
}) {
  const body = content ?? text;
  const anchorRef = useRef(null);
  const [tip, setTip] = useState({ open: false, coords: null });

  const readCoords = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return null;
    return getOdv2TooltipCoords(el.getBoundingClientRect(), placement);
  }, [placement]);

  const show = () => {
    const coords = readCoords();
    if (!coords) return;
    setTip({ open: true, coords });
  };

  const hide = () => setTip({ open: false, coords: null });

  const refreshCoords = useCallback(() => {
    const coords = readCoords();
    if (!coords) return;
    setTip((prev) => (prev.open ? { open: true, coords } : prev));
  }, [readCoords]);

  useLayoutEffect(() => {
    if (!tip.open) return undefined;
    refreshCoords();
    window.addEventListener('scroll', refreshCoords, true);
    window.addEventListener('resize', refreshCoords);
    return () => {
      window.removeEventListener('scroll', refreshCoords, true);
      window.removeEventListener('resize', refreshCoords);
    };
  }, [tip.open, refreshCoords]);

  const wrapClass = [
    'odv2-tooltip-wrap',
    selectable ? 'odv2-tooltip-wrap--selectable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <span
        ref={anchorRef}
        className={wrapClass}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {tip.open && tip.coords && body != null
        ? createPortal(
            <span
              className="odv2-tooltip-popover odv2-tooltip-popover--portal"
              style={tip.coords}
              role="tooltip"
            >
              {body}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
